// ============================================
// POS MAIN - Enterprise POS Foundation
// OpenOne-style dense, touch-optimized interface
// ============================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFranchise } from '@/context/FranchiseContext';
import { useAuth } from '@/context/AuthContext';
import { POSHeader } from './components/POSHeader';
import { TableGrid } from './components/TableGrid';
import { OrderPanel } from './components/OrderPanel';
import { MenuPanel } from './components/MenuPanel';
import { HeldOrders } from './components/HeldOrders';
import { PaymentModal } from './components/PaymentModal';
import { OrderTypeSelector } from './components/OrderTypeSelector';
import { menuItems, menuCategories, getItemPrice } from '@/data/menu';
import { getEffectiveVatRate, defaultVatRules } from '@/models/pricing';
import { posService, menuService } from '@/services/api';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

// ============================================
// ORDER TYPE CONSTANTS
// ============================================
export const ORDER_TYPES = {
  DINE_IN: 'dineIn',
  TAKEAWAY: 'takeaway',
  DELIVERY: 'delivery',
};

// ============================================
// POS VIEW MODES
// ============================================
const VIEW_MODES = {
  TABLES: 'tables',
  ORDER: 'order',
  HELD: 'held',
};

// ============================================
// SAMPLE HELD ORDERS (fallback for offline mode)
// ============================================
const sampleHeldOrders = [
  {
    id: 'HOLD-001',
    type: ORDER_TYPES.TAKEAWAY,
    customerName: 'Anders',
    items: [
      { id: 'chicken-tikka-masala', name: 'Chicken Tikka Masala', quantity: 2, price: 189 },
      { id: 'garlic-naan', name: 'Garlic Naan', quantity: 2, price: 45 },
    ],
    holdTime: new Date(Date.now() - 12 * 60000),
    note: 'Waiting for customer call',
  },
  {
    id: 'HOLD-002',
    type: ORDER_TYPES.DELIVERY,
    customerName: 'Maria K.',
    customerPhone: '+47 912 34 567',
    items: [
      { id: 'butter-chicken', name: 'Butter Chicken', quantity: 1, price: 189 },
      { id: 'lamb-biryani', name: 'Lamb Biryani', quantity: 1, price: 229 },
      { id: 'mango-lassi', name: 'Mango Lassi', quantity: 2, price: 59 },
    ],
    holdTime: new Date(Date.now() - 25 * 60000),
    note: 'Delivery at 18:30',
    deliveryAddress: 'Karl Johans gate 15, 0154 Oslo',
  },
];

// ============================================
// FALLBACK TABLE GENERATOR (offline mode)
// ============================================
const generateFallbackTables = () => {
  const tableConfigs = [
    { id: 'T1', tableNumber: 'T1', seats: 2, zone: 'main' },
    { id: 'T2', tableNumber: 'T2', seats: 4, zone: 'main' },
    { id: 'T3', tableNumber: 'T3', seats: 4, zone: 'main' },
    { id: 'T4', tableNumber: 'T4', seats: 6, zone: 'main' },
    { id: 'T5', tableNumber: 'T5', seats: 2, zone: 'main' },
    { id: 'T6', tableNumber: 'T6', seats: 4, zone: 'main' },
    { id: 'T7', tableNumber: 'T7', seats: 8, zone: 'main' },
    { id: 'T8', tableNumber: 'T8', seats: 4, zone: 'main' },
    { id: 'T9', tableNumber: 'T9', seats: 2, zone: 'window' },
    { id: 'T10', tableNumber: 'T10', seats: 6, zone: 'window' },
    { id: 'T11', tableNumber: 'T11', seats: 4, zone: 'window' },
    { id: 'T12', tableNumber: 'T12', seats: 2, zone: 'window' },
    { id: 'T13', tableNumber: 'T13', seats: 4, zone: 'outdoor' },
    { id: 'T14', tableNumber: 'T14', seats: 4, zone: 'outdoor' },
    { id: 'T15', tableNumber: 'T15', seats: 6, zone: 'outdoor' },
    { id: 'B1', tableNumber: 'B1', seats: 2, zone: 'bar' },
    { id: 'B2', tableNumber: 'B2', seats: 2, zone: 'bar' },
    { id: 'B3', tableNumber: 'B3', seats: 2, zone: 'bar' },
    { id: 'B4', tableNumber: 'B4', seats: 2, zone: 'bar' },
    { id: 'B5', tableNumber: 'B5', seats: 2, zone: 'bar' },
  ];

  return tableConfigs.map((table, index) => ({
    ...table,
    status: index === 1 || index === 3 || index === 7 ? 'occupied' : 
            index === 5 ? 'reserved' :
            index === 8 ? 'billing' : 'available',
    guests: index === 1 ? 3 : index === 3 ? 5 : index === 7 ? 4 : index === 8 ? 2 : 0,
    orderId: index === 1 ? 'ORD-001' : index === 3 ? 'ORD-002' : index === 7 ? 'ORD-003' : index === 8 ? 'ORD-004' : null,
    amount: index === 1 ? 856 : index === 3 ? 1420 : index === 7 ? 965 : index === 8 ? 1250 : 0,
    startTime: index === 1 ? new Date(Date.now() - 45 * 60000) : 
               index === 3 ? new Date(Date.now() - 72 * 60000) :
               index === 7 ? new Date(Date.now() - 28 * 60000) :
               index === 8 ? new Date(Date.now() - 95 * 60000) : null,
    reservationTime: index === 5 ? '19:30' : null,
  }));
};

// ============================================
// MAIN POS COMPONENT
// ============================================
export function POSMain() {
  const { 
    currentOutlet: franchiseOutlet, 
    currentUser: franchiseUser, 
    formatPrice, 
    getVatRate,
    checkPermission,
    Permission 
  } = useFranchise();
  
  // Get outlet from AuthContext (live data)
  const { currentOutlet: authOutlet, user } = useAuth();
  
  // Use auth outlet if available, fallback to franchise outlet
  const currentOutlet = authOutlet || franchiseOutlet;
  const currentUser = user || franchiseUser;

  // View state
  const [viewMode, setViewMode] = useState(VIEW_MODES.TABLES);
  const [selectedZone, setSelectedZone] = useState('all');
  
  // Table state
  const [selectedTable, setSelectedTable] = useState(null);
  const [tables, setTables] = useState([]);
  const [zones, setZones] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  
  // Order state
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderType, setOrderType] = useState(ORDER_TYPES.DINE_IN);
  const [heldOrders, setHeldOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Menu state
  const [liveMenuItems, setLiveMenuItems] = useState([]);
  const [liveMenuCategories, setLiveMenuCategories] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  
  // UI state
  const [showPayment, setShowPayment] = useState(false);
  const [showOrderTypeSelector, setShowOrderTypeSelector] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [error, setError] = useState(null);

  // Fetch floor plan (tables and zones) from backend
  const fetchFloorPlan = useCallback(async () => {
    if (!currentOutlet?.id) return;
    
    setLoadingTables(true);
    try {
      const floorPlan = await posService.getFloorPlan(currentOutlet.id);
      
      // Transform floor plan data to table format
      const allTables = [];
      const allZones = new Set(['all']);
      
      // Map backend table states to frontend status
      const mapTableState = (backendState) => {
        const stateMap = {
          'free': 'available',
          'occupied': 'occupied',
          'reserved': 'reserved',
          'dirty': 'billing', // dirty tables need attention, similar to billing
          'billing': 'billing',
        };
        return stateMap[backendState] || 'available';
      };

      floorPlan.floors?.forEach(floor => {
        floor.zones?.forEach(zone => {
          allZones.add(zone.name || zone.id);
          zone.tables?.forEach(table => {
            allTables.push({
              id: table.id,
              tableNumber: table.table_number,
              seats: table.capacity,
              zone: zone.name || 'main',
              status: mapTableState(table.state),
              guests: table.current_guest_count || 0,
              orderId: table.current_order_id,
              amount: 0, // Will be fetched with order
              startTime: null,
              reservationTime: null,
            });
          });
        });
      });
      
      setTables(allTables);
      setZones(Array.from(allZones));
      setError(null);
    } catch (err) {
      console.error('Failed to fetch floor plan:', err);
      // Fallback to generated tables
      setTables(generateFallbackTables());
      setZones(['all', 'main', 'window', 'outdoor', 'bar']);
      setError('Using offline table data');
    } finally {
      setLoadingTables(false);
    }
  }, [currentOutlet?.id]);

  // Fetch menu items from backend
  const fetchMenu = useCallback(async () => {
    if (!currentOutlet?.id) return;
    
    setLoadingMenu(true);
    try {
      const posMenu = await menuService.getPOSMenu(currentOutlet.id);
      
      // Transform to expected format
      const categories = posMenu.categories?.map(cat => ({
        id: cat.id,
        name: cat.name,
        icon: cat.icon || '🍽️',
      })) || [];
      
      const items = posMenu.items?.map(item => ({
        id: item.id,
        name: item.name,
        category: item.category_id,
        basePrice: parseFloat(item.price) || 0,
        description: item.description,
        allergens: item.allergens || [],
        preparationTime: item.preparation_time,
        available: item.is_available !== false,
      })) || [];
      
      if (categories.length > 0) {
        setLiveMenuCategories(categories);
      }
      if (items.length > 0) {
        setLiveMenuItems(items);
      }
    } catch (err) {
      console.error('Failed to fetch menu:', err);
      // Keep using static menu as fallback
    } finally {
      setLoadingMenu(false);
    }
  }, [currentOutlet?.id]);

  // Fetch held/open orders
  const fetchHeldOrders = useCallback(async () => {
    if (!currentOutlet?.id) return;
    
    setLoadingOrders(true);
    try {
      const orders = await posService.getOrders(currentOutlet.id, { status: 'held' });
      const formattedOrders = orders.items?.map(order => ({
        id: order.id,
        orderNumber: order.order_number,
        type: order.order_type === 'dine_in' ? ORDER_TYPES.DINE_IN :
              order.order_type === 'takeaway' ? ORDER_TYPES.TAKEAWAY : ORDER_TYPES.DELIVERY,
        customerName: order.customer_name || 'Guest',
        customerPhone: order.customer_phone,
        items: order.lines?.map(line => ({
          id: line.id,
          name: line.product_name,
          quantity: line.quantity,
          price: parseFloat(line.unit_price),
        })) || [],
        holdTime: order.ordered_at ? new Date(order.ordered_at) : new Date(),
        note: order.internal_notes,
        deliveryAddress: order.delivery_address,
      })) || [];
      
      setHeldOrders(formattedOrders);
    } catch (err) {
      console.error('Failed to fetch held orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  }, [currentOutlet?.id]);

  // Initial data fetch
  useEffect(() => {
    fetchFloorPlan();
    fetchMenu();
    fetchHeldOrders();
  }, [fetchFloorPlan, fetchMenu, fetchHeldOrders]);

  // Helper to update a specific table
  const updateTable = useCallback((tableId, updates) => {
    setTables(prev => prev.map(t => 
      t.id === tableId ? { ...t, ...updates } : t
    ));
  }, []);

  // Update table state on backend
  const updateTableState = useCallback(async (tableId, state, guestCount) => {
    try {
      await posService.updateTableState(tableId, state, guestCount);
      updateTable(tableId, { status: state, guests: guestCount });
    } catch (err) {
      console.error('Failed to update table state:', err);
      // Still update locally for responsiveness
      updateTable(tableId, { status: state, guests: guestCount });
    }
  }, [updateTable]);

  // Create order on backend
  const createOrderOnBackend = useCallback(async (orderData) => {
    if (!currentOutlet?.id) return null;
    
    try {
      const tenantId = user?.tenant_id || currentOutlet.tenant_id;
      const response = await posService.createOrder({
        tenant_id: tenantId,
        outlet_id: currentOutlet.id,
        order_type: orderType === ORDER_TYPES.DINE_IN ? 'dine_in' :
                   orderType === ORDER_TYPES.TAKEAWAY ? 'takeaway' : 'delivery',
        table_id: orderData.tableId,
        guest_count: orderData.guestCount || 1,
        customer_name: orderData.customerName,
        customer_phone: orderData.customerPhone,
        currency: currentOutlet.currency || 'NOK',
        lines: orderData.items?.map(item => ({
          product_name: item.name,
          product_code: item.id,
          category: item.category || 'food',
          quantity: item.quantity,
          unit_price: item.price,
          vat_rate: 0, // Server will calculate
        })) || [],
      });
      return response;
    } catch (err) {
      console.error('Failed to create order:', err);
      return null;
    }
  }, [currentOutlet, orderType, user]);

  // Use live menu if available, fallback to static
  const activeMenuItems = liveMenuItems.length > 0 ? liveMenuItems : menuItems;
  const activeMenuCategories = liveMenuCategories.length > 0 ? liveMenuCategories : menuCategories;

  // Update clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate VAT for item
  const calculateItemVat = useCallback((item, type = orderType) => {
    if (!currentOutlet) return { rate: 0.25, label: 'MVA' };
    
    const vatInfo = getVatRate(type, item.vatCategory || 'food');
    return {
      rate: vatInfo.rate,
      label: currentOutlet?.address?.country === 'NO' ? 'MVA' : 'MOMS',
    };
  }, [currentOutlet, getVatRate, orderType]);

  // Calculate order totals
  const calculateOrderTotals = useCallback((items, type = orderType) => {
    if (!items || items.length === 0) {
      return { 
        subtotal: 0, 
        vatBreakdown: {}, 
        vatTotal: 0, 
        total: 0 
      };
    }

    const currency = currentOutlet?.currency || 'NOK';
    let subtotal = 0;
    const vatBreakdown = {};

    items.forEach(item => {
      const itemPrice = item.price || getItemPrice(menuItems.find(m => m.id === item.id), currency);
      const lineTotal = itemPrice * item.quantity;
      subtotal += lineTotal;

      const vatInfo = calculateItemVat(item, type);
      const vatKey = `${Math.round(vatInfo.rate * 100)}%`;
      
      if (!vatBreakdown[vatKey]) {
        vatBreakdown[vatKey] = { net: 0, vat: 0 };
      }
      vatBreakdown[vatKey].net += lineTotal;
      vatBreakdown[vatKey].vat += Math.round(lineTotal * vatInfo.rate);
    });

    const vatTotal = Object.values(vatBreakdown).reduce((sum, v) => sum + v.vat, 0);
    const total = subtotal + vatTotal;

    return { subtotal, vatBreakdown, vatTotal, total };
  }, [currentOutlet, calculateItemVat, orderType]);

  // Order totals - defined after calculateOrderTotals
  const orderTotals = useMemo(() => {
    return calculateOrderTotals(currentOrder?.items || [], orderType);
  }, [currentOrder?.items, orderType, calculateOrderTotals]);

  // Handle table selection
  const handleTableSelect = useCallback(async (table) => {
    setSelectedTable(table);
    
    if (table.status === 'occupied' || table.status === 'billing') {
      // Load existing order from backend
      if (table.orderId) {
        try {
          const order = await posService.getOrder(table.orderId);
          setCurrentOrder({
            id: order.id,
            orderNumber: order.order_number,
            tableId: table.id,
            type: ORDER_TYPES.DINE_IN,
            guests: order.guest_count || table.guests,
            items: order.lines?.map(line => ({
              id: line.product_id || line.id,
              name: line.product_name,
              quantity: line.quantity,
              price: parseFloat(line.unit_price),
              vatCategory: line.category || 'food',
            })) || [],
            startTime: order.ordered_at ? new Date(order.ordered_at) : table.startTime,
            total: parseFloat(order.total) || 0,
          });
        } catch (err) {
          console.error('Failed to fetch order:', err);
          // Fallback to basic order data
          setCurrentOrder({
            id: table.orderId,
            tableId: table.id,
            type: ORDER_TYPES.DINE_IN,
            guests: table.guests,
            items: [],
            startTime: table.startTime,
          });
        }
      } else {
        setCurrentOrder({
          id: `LOCAL-${Date.now()}`,
          tableId: table.id,
          type: ORDER_TYPES.DINE_IN,
          guests: table.guests,
          items: [],
          startTime: table.startTime,
        });
      }
      setOrderType(ORDER_TYPES.DINE_IN);
      setViewMode(VIEW_MODES.ORDER);
    } else if (table.status === 'available') {
      // Start new order
      setCurrentOrder(null);
      setShowOrderTypeSelector(true);
    }
  }, []);

  // Start new order
  const handleStartOrder = useCallback(async (type, tableId = null, guests = 0) => {
    const newOrder = {
      id: `ORD-${Date.now()}`,
      tableId,
      type,
      guests,
      items: [],
      startTime: new Date(),
    };
    setCurrentOrder(newOrder);
    setOrderType(type);
    setShowOrderTypeSelector(false);
    setViewMode(VIEW_MODES.ORDER);

    // Update table status if dine-in
    if (tableId && type === ORDER_TYPES.DINE_IN) {
      await updateTableState(tableId, 'occupied', guests);
      updateTable(tableId, { 
        orderId: newOrder.id, 
        startTime: new Date() 
      });
    }
  }, [updateTable, updateTableState]);

  // Add item to order
  const handleAddItem = useCallback((menuItem) => {
    if (!currentOrder) return;

    const currency = currentOutlet?.currency || 'NOK';
    const price = getItemPrice(menuItem, currency);

    setCurrentOrder(prev => {
      const existingIndex = prev.items.findIndex(i => i.id === menuItem.id);
      
      if (existingIndex >= 0) {
        const newItems = [...prev.items];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newItems[existingIndex].quantity + 1,
        };
        return { ...prev, items: newItems };
      }
      
      return {
        ...prev,
        items: [...prev.items, {
          id: menuItem.id,
          name: menuItem.name,
          quantity: 1,
          price,
          vatCategory: menuItem.vatCategory,
        }],
      };
    });
  }, [currentOrder, currentOutlet]);

  // Update item quantity
  const handleUpdateQuantity = useCallback((itemId, delta) => {
    setCurrentOrder(prev => {
      if (!prev) return null;
      
      const newItems = prev.items.map(item => {
        if (item.id === itemId) {
          const newQty = Math.max(0, item.quantity + delta);
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(item => item.quantity > 0);

      return { ...prev, items: newItems };
    });
  }, []);

  // Remove item from order
  const handleRemoveItem = useCallback((itemId) => {
    setCurrentOrder(prev => {
      if (!prev) return null;
      return {
        ...prev,
        items: prev.items.filter(item => item.id !== itemId),
      };
    });
  }, []);

  // Hold current order
  const handleHoldOrder = useCallback((note = '') => {
    if (!currentOrder || currentOrder.items.length === 0) return;

    const heldOrder = {
      ...currentOrder,
      holdTime: new Date(),
      note,
      id: `HOLD-${Date.now()}`,
    };

    setHeldOrders(prev => [...prev, heldOrder]);
    
    // Free table if dine-in
    if (currentOrder.tableId) {
      updateTable(currentOrder.tableId, { 
        status: 'available', 
        guests: 0, 
        orderId: null, 
        amount: 0, 
        startTime: null 
      });
    }

    setCurrentOrder(null);
    setSelectedTable(null);
    setViewMode(VIEW_MODES.TABLES);
  }, [currentOrder, updateTable]);

  // Resume held order
  const handleResumeOrder = useCallback((heldOrder) => {
    setCurrentOrder({
      ...heldOrder,
      id: `ORD-${Date.now()}`,
    });
    setOrderType(heldOrder.type);
    setHeldOrders(prev => prev.filter(o => o.id !== heldOrder.id));
    setViewMode(VIEW_MODES.ORDER);
  }, []);

  // Delete held order
  const handleDeleteHeldOrder = useCallback((orderId) => {
    setHeldOrders(prev => prev.filter(o => o.id !== orderId));
  }, []);

  // Cancel current order
  const handleCancelOrder = useCallback(() => {
    if (currentOrder?.tableId) {
      updateTable(currentOrder.tableId, { 
        status: 'available', 
        guests: 0, 
        orderId: null, 
        amount: 0, 
        startTime: null 
      });
    }
    setCurrentOrder(null);
    setSelectedTable(null);
    setViewMode(VIEW_MODES.TABLES);
  }, [currentOrder, updateTable]);

  // Process payment
  const handlePayment = useCallback(async (paymentMethod) => {
    if (!currentOrder) return;
    
    try {
      // If the order was created locally, create it on the backend first
      if (currentOrder.id.startsWith('ORD-') || currentOrder.id.startsWith('LOCAL-')) {
        const backendOrder = await createOrderOnBackend({
          tableId: currentOrder.tableId,
          guestCount: currentOrder.guests,
          items: currentOrder.items,
        });
        
        if (backendOrder) {
          // Process payment
          await posService.addPayment(backendOrder.id, {
            payment_method: paymentMethod,
            amount: orderTotals.total,
            currency: currentOutlet?.currency || 'NOK',
          });
        }
      } else {
        // Order already exists on backend
        await posService.addPayment(currentOrder.id, {
          payment_method: paymentMethod,
          amount: orderTotals.total,
          currency: currentOutlet?.currency || 'NOK',
        });
      }
    } catch (err) {
      console.error('Payment processing error:', err);
      // Continue with local state update even if backend fails
    }
    
    if (currentOrder?.tableId) {
      await updateTableState(currentOrder.tableId, 'available', 0);
      updateTable(currentOrder.tableId, { 
        orderId: null, 
        amount: 0, 
        startTime: null 
      });
    }

    setShowPayment(false);
    setCurrentOrder(null);
    setSelectedTable(null);
    setViewMode(VIEW_MODES.TABLES);
  }, [currentOrder, updateTable, updateTableState, createOrderOnBackend, orderTotals, currentOutlet]);

  // Quick takeaway order
  const handleQuickTakeaway = useCallback(() => {
    handleStartOrder(ORDER_TYPES.TAKEAWAY, null, 0);
  }, [handleStartOrder]);

  // Quick delivery order
  const handleQuickDelivery = useCallback(() => {
    handleStartOrder(ORDER_TYPES.DELIVERY, null, 0);
  }, [handleStartOrder]);

  // Check manager permissions
  const isManager = useMemo(() => {
    return checkPermission(Permission.POS_VOID_ORDER) || 
           checkPermission(Permission.POS_DISCOUNT) ||
           checkPermission(Permission.POS_REFUND);
  }, [checkPermission, Permission]);

  // Available zones - use live zones if available
  const availableZones = useMemo(() => {
    if (zones.length > 1) return zones;
    const uniqueZones = [...new Set(tables.map(t => t.zone))].filter(Boolean);
    return ['all', ...uniqueZones];
  }, [tables, zones]);

  // Filtered tables
  const filteredTables = useMemo(() => {
    if (selectedZone === 'all') return tables;
    return tables.filter(t => t.zone === selectedZone);
  }, [tables, selectedZone]);

  return (
    <div data-testid="pos-main" className="h-full flex flex-col bg-gray-100">
      {/* POS Header */}
      <POSHeader
        currentTime={currentTime}
        outlet={currentOutlet}
        user={currentUser}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        heldOrdersCount={heldOrders.length}
        onQuickTakeaway={handleQuickTakeaway}
        onQuickDelivery={handleQuickDelivery}
        isManager={isManager}
      />

      {/* Main Content */}
      <div className="flex-1 flex min-h-0 p-2 gap-2">
        {viewMode === VIEW_MODES.TABLES && (
          <>
            {/* Table Grid */}
            <div className="flex-1 flex flex-col">
              {loadingTables ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Loading floor plan...</p>
                  </div>
                </div>
              ) : (
                <TableGrid
                  tables={filteredTables}
                  selectedTable={selectedTable}
                  onTableSelect={handleTableSelect}
                  zones={availableZones}
                  selectedZone={selectedZone}
                  onZoneChange={setSelectedZone}
                  formatPrice={formatPrice}
                />
              )}
              {error && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                  {error}
                </div>
              )}
            </div>

            {/* Quick Actions Panel */}
            <div className="w-80 flex flex-col gap-2">
              {/* Quick Order Types */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Order</h3>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="btn-quick-takeaway"
                    onClick={handleQuickTakeaway}
                    className="flex flex-col items-center justify-center p-4 bg-amber-50 hover:bg-amber-100 rounded-lg border-2 border-amber-200 transition-colors"
                  >
                    <span className="text-2xl mb-1">🥡</span>
                    <span className="text-sm font-medium text-amber-800">Takeaway</span>
                  </button>
                  <button
                    data-testid="btn-quick-delivery"
                    onClick={handleQuickDelivery}
                    className="flex flex-col items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border-2 border-blue-200 transition-colors"
                  >
                    <span className="text-2xl mb-1">🛵</span>
                    <span className="text-sm font-medium text-blue-800">Delivery</span>
                  </button>
                </div>
              </div>

              {/* Held Orders Summary */}
              {heldOrders.length > 0 && (
                <div 
                  data-testid="held-orders-summary"
                  className="bg-white rounded-lg p-3 shadow-sm cursor-pointer hover:bg-gray-50"
                  onClick={() => setViewMode(VIEW_MODES.HELD)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700">Held Orders</h3>
                    <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-1 rounded-full">
                      {heldOrders.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {heldOrders.slice(0, 3).map(order => {
                      const elapsed = Math.round((currentTime.getTime() - order.holdTime.getTime()) / 60000);
                      return (
                        <div key={order.id} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            {order.customerName || order.type}
                          </span>
                          <span className="text-gray-400 text-xs">
                            {elapsed}m ago
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="bg-white rounded-lg p-3 shadow-sm flex-1">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Today&apos;s Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Open Tables</span>
                    <span className="font-mono font-semibold">
                      {tables.filter(t => t.status === 'occupied' || t.status === 'billing').length}/{tables.length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Held Orders</span>
                    <span className="font-mono font-semibold">{heldOrders.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Revenue</span>
                    <span className="font-mono font-semibold text-spice-cardamom">
                      {formatPrice(tables.reduce((sum, t) => sum + (t.amount || 0), 0))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {viewMode === VIEW_MODES.ORDER && (
          <>
            {/* Menu Panel */}
            <div className="flex-1">
              <MenuPanel
                categories={menuCategories}
                items={menuItems}
                onItemSelect={handleAddItem}
                currency={currentOutlet?.currency || 'NOK'}
              />
            </div>

            {/* Order Panel */}
            <div className="w-96">
              <OrderPanel
                order={currentOrder}
                orderType={orderType}
                totals={orderTotals}
                table={selectedTable}
                onUpdateQuantity={handleUpdateQuantity}
                onRemoveItem={handleRemoveItem}
                onHoldOrder={handleHoldOrder}
                onCancelOrder={handleCancelOrder}
                onPayment={() => setShowPayment(true)}
                formatPrice={formatPrice}
                isManager={isManager}
                vatLabel={currentOutlet?.address?.country === 'NO' ? 'MVA' : 'MOMS'}
              />
            </div>
          </>
        )}

        {viewMode === VIEW_MODES.HELD && (
          <HeldOrders
            orders={heldOrders}
            onResumeOrder={handleResumeOrder}
            onDeleteOrder={handleDeleteHeldOrder}
            onBack={() => setViewMode(VIEW_MODES.TABLES)}
            formatPrice={formatPrice}
            calculateTotals={calculateOrderTotals}
          />
        )}
      </div>

      {/* Order Type Selector Modal */}
      {showOrderTypeSelector && (
        <OrderTypeSelector
          table={selectedTable}
          onSelect={handleStartOrder}
          onClose={() => setShowOrderTypeSelector(false)}
        />
      )}

      {/* Payment Modal */}
      {showPayment && currentOrder && (
        <PaymentModal
          order={currentOrder}
          totals={orderTotals}
          onPayment={handlePayment}
          onClose={() => setShowPayment(false)}
          formatPrice={formatPrice}
          vatLabel={currentOutlet?.address?.country === 'NO' ? 'MVA' : 'MOMS'}
          isManager={isManager}
          outletId={currentOutlet?.id}
        />
      )}
    </div>
  );
}

export default POSMain;
